import { UNLOCK_TYPE_LABELS, type UnlockType } from '@game-keys/shared'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useDeleted, useRestoreGame, useRestoreKey } from '../lib/admin-api'
import { initialsFromTitle, tintFromString } from '../lib/tint'

export const Route = createFileRoute('/admin/deleted')({
  component: DeletedPage,
})

/**
 * The recycle bin.
 *
 * Soft deletes are only worth having if you can see and undo them — data
 * that is merely hidden, with no way back, is worse than data that is gone,
 * because you believe you still have it.
 */
function DeletedPage() {
  const deleted = useDeleted()

  const games = deleted.data?.games ?? []
  const keys = deleted.data?.orphanKeys ?? []

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold tracking-tight">Deleted</h1>
      <p className="mt-1 text-sm text-ink-soft">
        Nothing here is really gone. Removing a game or a key only marks it — restoring puts it
        back exactly as it was, keys included.
      </p>

      {deleted.isPending ? (
        <p className="mt-8 text-sm text-ink-muted">Loading…</p>
      ) : games.length === 0 && keys.length === 0 ? (
        <p className="mt-8 rounded-xl border border-dashed border-line-strong px-6 py-16 text-center text-sm text-ink-soft">
          Nothing has been deleted.
        </p>
      ) : (
        <>
          {games.length > 0 ? (
            <section className="mt-8">
              <h2 className="font-display text-lg font-semibold tracking-tight">Games</h2>
              <ul className="mt-3 overflow-hidden rounded-xl border border-line">
                {games.map((game) => (
                  <DeletedGameRow key={game.id} game={game} />
                ))}
              </ul>
            </section>
          ) : null}

          {/* Listed rather than counted. These belong to games that are still
              live, so they appear nowhere else in the admin — the library only
              shows live keys, which made a pointer to it a dead end. */}
          {keys.length > 0 ? (
            <section className="mt-10">
              <h2 className="font-display text-lg font-semibold tracking-tight">Keys</h2>
              <p className="mt-1 text-sm text-ink-soft">
                Deleted on their own, from games that are still in the library.
              </p>
              <ul className="mt-3 overflow-hidden rounded-xl border border-line">
                {keys.map((entry) => (
                  <DeletedKeyRow key={entry.id} entry={entry} />
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
    </div>
  )
}

type DeletedGame = NonNullable<ReturnType<typeof useDeleted>['data']>['games'][number]
type DeletedKey = NonNullable<ReturnType<typeof useDeleted>['data']>['orphanKeys'][number]

function DeletedGameRow({ game }: { game: DeletedGame }) {
  const restore = useRestoreGame()

  return (
    <li className="flex items-center gap-3 border-b border-line bg-surface-1 px-3 py-2.5 last:border-b-0">
      <span
        className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded opacity-60"
        style={{ backgroundColor: tintFromString(game.title) }}
      >
        {game.capsuleUrl ? (
          <img src={game.capsuleUrl} alt="" className="size-full object-cover" />
        ) : (
          <span className="font-display text-[10px] font-semibold text-white/40">
            {initialsFromTitle(game.title)}
          </span>
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-ink-soft">{game.title}</span>
        <span className="block text-xs text-ink-muted">
          deleted {new Date(game.deletedAt).toLocaleString()}
          {game.keyCount > 0
            ? ` · ${game.keyCount} ${game.keyCount === 1 ? 'key' : 'keys'} would return`
            : ''}
        </span>
      </span>

      <RestoreButton
        pending={restore.isPending}
        error={restore.error}
        onClick={() => restore.mutate(game.id)}
      />
    </li>
  )
}

function DeletedKeyRow({ entry }: { entry: DeletedKey }) {
  const restore = useRestoreKey()

  const unlock =
    entry.edition ?? (entry.unlockType === 'base' ? null : UNLOCK_TYPE_LABELS[entry.unlockType as UnlockType])

  return (
    <li className="flex flex-wrap items-center gap-3 border-b border-line bg-surface-1 px-3 py-2.5 last:border-b-0">
      <span
        className="size-2 shrink-0 rounded-full"
        style={{ backgroundColor: entry.storeColor }}
        title={entry.storeName}
      />

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-ink-soft">
          <Link
            to="/admin/game/$id"
            params={{ id: entry.gameId }}
            className="transition-colors hover:text-accent"
          >
            {entry.gameTitle}
          </Link>
        </span>
        <span className="block text-xs text-ink-muted">
          {entry.storeName}
          {unlock ? ` · ${unlock}` : ''}
          {entry.note ? ` · ${entry.note}` : ''} · deleted{' '}
          {new Date(entry.deletedAt).toLocaleString()}
        </span>
      </span>

      <RestoreButton
        pending={restore.isPending}
        error={restore.error}
        onClick={() => restore.mutate(entry.id)}
      />
    </li>
  )
}

function RestoreButton({
  pending,
  error,
  onClick,
}: {
  pending: boolean
  error: unknown
  onClick: () => void
}) {
  return (
    <div className="flex items-center gap-2">
      {error ? (
        <span role="alert" className="text-xs text-bad">
          {(error as Error).message}
        </span>
      ) : null}
      <button
        type="button"
        disabled={pending}
        onClick={onClick}
        className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-good/50 hover:text-good disabled:opacity-40"
      >
        {pending ? 'Restoring…' : 'Restore'}
      </button>
    </div>
  )
}
